// src/pages/membres/DetailsMembre.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '../../utils/formatters';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne avec bande secondaire */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  /* Cartes */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }
  .card-modern.border-secondary {
    border-top-color: ${COLORS.secondary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
    margin-right: 0.5rem;
  }
  .card-body-modern {
    padding: 1.2rem;
  }

  /* Avatar */
  .member-avatar-large {
    width: 110px;
    height: 110px;
    border-radius: 4px;
    background: #eef2f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    border: 3px solid ${COLORS.primary};
    font-size: 2.8rem;
    font-weight: 700;
    color: ${COLORS.primary};
    overflow: hidden;
    object-fit: cover;
  }
  .member-avatar-large img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Badges de rôle */
  .badge-role {
    padding: 0.25rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f1f4f9;
    color: #475569;
    border: 1px solid #e2e8f0;
  }
  .badge-role i {
    font-size: 0.9rem;
  }
  .badge-role-pasteur { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
  .badge-role-ancien { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
  .badge-role-diacre { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
  .badge-role-diaconesse { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
  .badge-role-jeune { background: #fffbeb; color: #d97706; border-color: #fde68a; }
  .badge-role-nouveau { background: #eef2ff; color: #4f46e5; border-color: #c7d2fe; }
  .badge-role-dp { background: #f3e8ff; color: #7c3aed; border-color: #d8b4fe; }
  .badge-role-membre { background: #f8fafc; color: #64748b; border-color: #e2e8f0; }

  /* Items d'information */
  .info-item {
    padding: 0.6rem 0.8rem;
    background: #fafbfc;
    border-left: 2px solid ${COLORS.secondary};
    border-radius: 2px;
    margin-bottom: 0.5rem;
  }
  .info-item small {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #94a3b8;
    font-weight: 600;
    display: block;
  }
  .info-item span {
    font-size: 0.9rem;
    color: #1e293b;
    font-weight: 500;
  }

  /* Cartes spirituelles */
  .spiritual-item {
    background: #fafbfc;
    border: 1px solid #e9edf2;
    padding: 0.8rem 0.5rem;
    text-align: center;
    transition: background 0.2s;
    border-radius: 2px;
  }
  .spiritual-item:hover {
    background: #f1f5f9;
  }
  .spiritual-item i {
    font-size: 1.8rem;
    display: block;
    margin-bottom: 0.3rem;
  }
  .spiritual-item .label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 600;
    letter-spacing: 0.3px;
  }
  .spiritual-item .value {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.85rem;
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .member-avatar-large {
      width: 80px;
      height: 80px;
      font-size: 2rem;
    }
  }
`;

export default function DetailsMembre() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [membre, setMembre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMembre();
  }, [id]);

  const fetchMembre = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/membre_details.php?user_id=${userId}&id=${id}`, {
        headers: { 'Accept': 'application/json' }
      });

      const result = await response.json();

      if (result.success) {
        setMembre(result.membre);
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    const map = {
      'Pasteur': 'badge-role-pasteur',
      'Ancien': 'badge-role-ancien',
      'Diacre': 'badge-role-diacre',
      'Diaconesse': 'badge-role-diaconesse',
      'Jeune': 'badge-role-jeune',
      'Nouveau': 'badge-role-nouveau',
      'DP': 'badge-role-dp',
      'Membre': 'badge-role-membre'
    };
    return map[role] || 'badge-role-membre';
  };

  const getRoleIcon = (role) => {
    const icons = {
      'Pasteur': 'ri-user-star-line',
      'Ancien': 'ri-user-settings-line',
      'Diacre': 'ri-user-heart-line',
      'Diaconesse': 'ri-user-heart-line',
      'Jeune': 'ri-user-smile-line',
      'Nouveau': 'ri-user-add-line',
      'DP': 'ri-user-follow-line',
      'Membre': 'ri-user-line'
    };
    return icons[role] || 'ri-user-line';
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="loading-wrapper">
            <div className="text-center">
              <div className="loading-ring-wrapper mb-3">
                <div className="loading-ring"></div>
                <div className="loading-ring-inner"></div>
              </div>
              <h5 className="fw-bold" style={{ color: COLORS.primaryDark }}>Chargement...</h5>
              <p className="text-muted small">Récupération des informations</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !membre) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="container-fluid">
            <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: '4px' }}>
              <i className="ri-error-warning-line fs-4 me-2"></i>
              {error || 'Membre non trouvé'}
              <hr />
              <Button variant="outline-danger" size="sm" onClick={() => navigate('/membres/liste')} style={{ borderRadius: '4px' }}>
                <i className="ri-arrow-left-line me-1"></i>Retour
              </Button>
            </Alert>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* En-tête */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-user-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Détails du membre</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-fingerprint-line"></i> {membre.matricule}
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> Inscrit le {formatDate(membre.date_creation)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" className="btn-modern-outline" onClick={() => navigate(-1)}>
                <i className="ri-arrow-left-line"></i> Retour
              </Button>
              <Button variant="primary" size="sm" className="btn-modern-primary" onClick={() => navigate(`/membres/modifier/${membre.id}`)}>
                <i className="ri-edit-line"></i> Modifier
              </Button>
            </div>
          </div>

          {/* Bloc principal */}
          <Row className="g-4">
            {/* Profil */}
            <Col md={4}>
              <Card className="card-modern border-primary h-100">
                <Card.Body className="card-body-modern text-center">
                  <div className="member-avatar-large">
                    {membre?.avatar ? (
                      <img src={`https://josephsbusiness.com/assets/images/${membre.avatar}`} alt="Avatar" />
                    ) : (
                      <span>{membre.prenom?.charAt(0)}{membre.nom?.charAt(0)}</span>
                    )}
                  </div>
                  <h5 className="fw-bold mt-3 mb-1">{membre.prenom} {membre.nom}</h5>
                  <span className={`badge-role ${getRoleBadgeClass(membre.role)}`}>
                    <i className={getRoleIcon(membre.role)}></i> {membre.role}
                  </span>
                  <hr className="my-3" />
                  <div className="text-start small">
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Genre</span>
                      <span className="fw-semibold">{membre.gendre}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Naissance</span>
                      <span className="fw-semibold">{membre.datenaiss && membre.datenaiss !== '0000-00-00' ? formatDate(membre.datenaiss) : '-'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Lieu</span>
                      <span className="fw-semibold">{membre.lieunaiss || '-'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Situation</span>
                      <span className="fw-semibold">{membre.situtaion_maritale || '-'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Enfants</span>
                      <span className="fw-semibold">{membre.nb_enf || 0}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Infos personnelles */}
            <Col md={8}>
              <Card className="card-modern border-secondary h-100">
                <Card.Header className="card-header-modern">
                  <i className="ri-user-line"></i> Coordonnées & affectation
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <Row>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Contact</small>
                        <span>{membre.contact || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Email</small>
                        <span>{membre.email || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Église</small>
                        <span>{membre.nom_eglise || membre.matri_eglise || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Adresse église</small>
                        <span>{membre.adresse_eglise || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Lieu d'affectation</small>
                        <span>{membre.lieu_affectation || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="info-item">
                        <small>Fonction</small>
                        <span>{membre.fonct_org || '-'}</span>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Section spirituelle */}
          <Row className="mt-4">
            <Col md={12}>
              <Card className="card-modern border-secondary">
                <Card.Header className="card-header-modern">
                  <i className="ri-chat-heart-line"></i> Informations spirituelles
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <Row className="g-3">
                    <Col md={3} sm={6}>
                      <div className="spiritual-item">
                        <i className="ri-drop-line" style={{ color: '#0dcaf0' }}></i>
                        <div className="label">Baptême d'eau</div>
                        <div className="value">{membre.date_bapt_eau && membre.date_bapt_eau !== '0000-00-00' ? formatDate(membre.date_bapt_eau) : 'Non baptisé'}</div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="spiritual-item">
                        <i className="ri-flashlight-line" style={{ color: '#f59e0b' }}></i>
                        <div className="label">Baptême du Saint-Esprit</div>
                        <div className="value">{membre.date_bapt_saint && membre.date_bapt_saint !== '0000-00-00' ? formatDate(membre.date_bapt_saint) : 'Non baptisé'}</div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="spiritual-item">
                        <i className="ri-calendar-check-line" style={{ color: COLORS.success }}></i>
                        <div className="label">Consécration</div>
                        <div className="value">{membre.date_consecration && membre.date_consecration !== '0000-00-00' ? formatDate(membre.date_consecration) : '-'}</div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="spiritual-item">
                        <i className="ri-flag-line" style={{ color: COLORS.secondary }}></i>
                        <div className="label">Rôle</div>
                        <div className="value">{membre.role}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Formations */}
          <Row className="mt-4">
            <Col md={6}>
              <Card className="card-modern">
                <Card.Header className="card-header-modern">
                  <i className="ri-graduation-cap-line"></i> Formation séculière
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <p className="mb-0">{membre.form_seculiere || 'Aucune information'}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-modern">
                <Card.Header className="card-header-modern">
                  <i className="ri-book-open-line"></i> Formation ecclésiastique
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <p className="mb-0">{membre.form_eccle || 'Aucune information'}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}